import { getAnalyticsData, getAnalyticsSummary } from '../api/Optimise';
import { AnalyticsDataItem, AnalyticsRangeItem, AnalyticsRangeResponse } from '../types/optimise';

import { UserSub } from './apiRequest';
import {
  addDays,
  endOfMonth,
  endOfWeek,
  formatDate,
  formatISODate,
  fromUnixTime,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from './dateUtils';

export type FinancesPeriod = 'day' | 'week' | 'month' | 'year';

export interface FinancialAggregate {
  exportEarnings: number; // pence (positive)
  importCosts: number; // pence (positive)
  optimiseSaving: number; // same unit as getSavingsTotal (pounds or pence — matches SavingsSummary)
}

export interface PeriodBar {
  label: string;
  isoDate: string;
  exportEarnings: number; // pence
  importCosts: number; // pence
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Aggregate raw AnalyticsDataItem[] into financial totals.
 *  Raw API: actual_export_earning is NEGATIVE; actual_import_cost is POSITIVE. */
export const aggregateRawItems = (items: AnalyticsDataItem[]): FinancialAggregate => {
  let exportEarnings = 0;
  let importCosts = 0;
  let smartBattery = 0;
  let dumbBattery = 0;

  for (const item of items) {
    exportEarnings += -toNumber(item.actual_export_earning);
    importCosts += toNumber(item.actual_import_cost);
    smartBattery += toNumber(item.battery_earning) - toNumber(item.battery_cost);
    dumbBattery += toNumber(item.dumb_battery_earning) - toNumber(item.dumb_battery_cost);
  }

  return { exportEarnings, importCosts, optimiseSaving: smartBattery - dumbBattery };
};

/** Aggregate AnalyticsRangeItem[] (from the range endpoint) into financial totals. */
export const aggregateRangeItems = (items: AnalyticsRangeItem[]): FinancialAggregate => {
  let exportEarnings = 0;
  let importCosts = 0;
  let smartBattery = 0;
  let dumbBattery = 0;

  for (const item of items) {
    exportEarnings += -parseFloat(item.actual_export_earning);
    importCosts += parseFloat(item.actual_import_cost);
    smartBattery += parseFloat(item.battery_earning) - parseFloat(item.battery_cost);
    dumbBattery += parseFloat(item.dumb_battery_earning) - parseFloat(item.dumb_battery_cost);
  }

  return { exportEarnings, importCosts, optimiseSaving: smartBattery - dumbBattery };
};

/** Yesterday — the last calendar day that can have analytics data. */
const clampToYesterday = (date: Date): Date => {
  const yest = subDays(new Date(), 1);
  return date > yest ? yest : date;
};

/** Start and end ISO dates for the period containing `date`, capped at yesterday. */
export const getPeriodDateRange = (
  date: Date,
  period: Exclude<FinancesPeriod, 'day'>
): { startDate: string; endDate: string } => {
  switch (period) {
    case 'week':
      return {
        startDate: formatISODate(startOfWeek(date, { weekStartsOn: 1 })),
        endDate: formatISODate(clampToYesterday(endOfWeek(date, { weekStartsOn: 1 }))),
      };
    case 'month':
      return {
        startDate: formatISODate(startOfMonth(date)),
        endDate: formatISODate(clampToYesterday(endOfMonth(date))),
      };
    case 'year':
      return {
        startDate: formatISODate(new Date(date.getFullYear(), 0, 1)),
        endDate: formatISODate(clampToYesterday(new Date(date.getFullYear(), 11, 31))),
      };
  }
};

const itemToFinancials = (item: AnalyticsRangeItem | undefined) => ({
  exportEarnings: item ? -parseFloat(item.actual_export_earning) : 0,
  importCosts: item ? parseFloat(item.actual_import_cost) : 0,
});

/** Fetch analytics for a date range in a single request. */
export const fetchAnalyticsRange = async (
  resolution: Exclude<FinancesPeriod, 'day'>,
  startDate: string,
  endDate: string,
  userSub: UserSub
): Promise<AnalyticsRangeResponse> => {
  return getAnalyticsSummary(resolution, startDate, endDate, userSub);
};

/** Fetch half-hourly analytics for a single day. */
export const fetchAnalyticsDay = async (date: string, userSub: UserSub): Promise<AnalyticsDataItem[]> => {
  return getAnalyticsData(date, userSub);
};

/** Build bars for the Week view: 7 bars Mon–Sun, zero-filled for any missing days. */
export const buildWeekBars = (items: AnalyticsRangeItem[], anchorDate: Date): PeriodBar[] => {
  const byIso = new Map(
    items.map((item) => {
      const d = fromUnixTime(item.start);
      return [formatISODate(d), item];
    })
  );
  const weekStart = startOfWeek(anchorDate, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    const iso = formatISODate(date);
    return {
      label: formatDate(date, 'EEE'),
      isoDate: iso,
      ...itemToFinancials(byIso.get(iso)),
    };
  });
};

/** Build bars for the Month view: one bar per calendar day, zero-filled for any missing days. */
export const buildMonthBars = (items: AnalyticsRangeItem[], anchorDate: Date): PeriodBar[] => {
  const byIso = new Map(
    items.map((item) => {
      const d = fromUnixTime(item.start);
      return [formatISODate(d), item];
    })
  );
  const monthStart = startOfMonth(anchorDate);
  const daysInMonth = endOfMonth(anchorDate).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => {
    const date = addDays(monthStart, i);
    const iso = formatISODate(date);
    return {
      label: formatDate(date, 'd'),
      isoDate: iso,
      ...itemToFinancials(byIso.get(iso)),
    };
  });
};

/** Build bars for the Year view: 12 bars Jan–Dec, zero-filled for any missing months. */
export const buildYearBars = (items: AnalyticsRangeItem[], anchorDate: Date): PeriodBar[] => {
  const byMonth = new Map(
    items.map((item) => {
      const d = fromUnixTime(item.start);
      return [d.getMonth(), item];
    })
  );
  const year = anchorDate.getFullYear();
  return Array.from({ length: 12 }, (_, m) => {
    const date = new Date(year, m, 1);
    return {
      label: formatDate(date, 'MMM'),
      isoDate: formatISODate(date),
      ...itemToFinancials(byMonth.get(m)),
    };
  });
};

/** Dispatch to the correct bar-builder for week/month/year. */
export const buildPeriodBars = (
  items: AnalyticsRangeItem[],
  period: Exclude<FinancesPeriod, 'day'>,
  anchorDate: Date
): PeriodBar[] => {
  switch (period) {
    case 'week':
      return buildWeekBars(items, anchorDate);
    case 'month':
      return buildMonthBars(items, anchorDate);
    case 'year':
      return buildYearBars(items, anchorDate);
  }
};

/** Human-readable label for the current period anchor. */
export const getPeriodLabel = (date: Date, period: FinancesPeriod): string => {
  switch (period) {
    case 'day':
      return formatDate(date, 'EEEE d MMMM yyyy');
    case 'week': {
      const start = startOfWeek(date, { weekStartsOn: 1 });
      const end = endOfWeek(date, { weekStartsOn: 1 });
      if (start.getFullYear() !== end.getFullYear()) {
        return `${formatDate(start, 'd MMMM yyyy')} - ${formatDate(end, 'd MMMM yyyy')}`;
      }
      if (start.getMonth() !== end.getMonth()) {
        return `${formatDate(start, 'd MMMM')} - ${formatDate(end, 'd MMMM yyyy')}`;
      }
      return `${formatDate(start, 'd')} - ${formatDate(end, 'd MMMM yyyy')}`;
    }
    case 'month':
      return formatDate(date, 'MMMM yyyy');
    case 'year':
      return formatDate(date, 'yyyy');
  }
};

/** Move one period backwards. */
export const prevPeriodDate = (date: Date, period: FinancesPeriod): Date => {
  switch (period) {
    case 'day':
      return subDays(date, 1);
    case 'week':
      return subWeeks(date, 1);
    case 'month':
      return subMonths(date, 1);
    case 'year':
      return subYears(date, 1);
  }
};

/** Move one period forwards. */
export const nextPeriodDate = (date: Date, period: FinancesPeriod): Date => {
  switch (period) {
    case 'day':
      return addDays(date, 1);
    case 'week':
      return addDays(date, 7);
    case 'month': {
      const next = new Date(date);
      next.setMonth(next.getMonth() + 1);
      return next;
    }
    case 'year': {
      const next = new Date(date);
      next.setFullYear(next.getFullYear() + 1);
      return next;
    }
  }
};

/** True when `date` is in the future (i.e., navigating forwards is disabled). */
export const isAtOrAfterToday = (date: Date, period: FinancesPeriod): boolean => {
  const today = new Date();
  switch (period) {
    case 'day':
      return date >= subDays(today, 1); // can't go past yesterday
    case 'week':
      return startOfWeek(date, { weekStartsOn: 1 }) >= startOfWeek(today, { weekStartsOn: 1 });
    case 'month':
      return (
        date.getFullYear() > today.getFullYear() ||
        (date.getFullYear() === today.getFullYear() && date.getMonth() >= today.getMonth())
      );
    case 'year':
      return date.getFullYear() >= today.getFullYear();
  }
};
